/**
 * Token store for plugin mode.
 * In plugin mode, auth is token-based (Bearer) instead of cookie-based.
 * The token is obtained via bridge SSO or password login and stored here.
 */

let accessToken: string | null = null

export function setPluginAccessToken(token: string | null) {
  accessToken = token
  if (token) {
    sessionStorage.setItem('curietech_access_token', token)
  } else {
    sessionStorage.removeItem('curietech_access_token')
  }
}

export function getPluginAccessToken(): string | null {
  if (accessToken) return accessToken
  return sessionStorage.getItem('curietech_access_token')
}

export function clearPluginAccessToken() {
  accessToken = null
  sessionStorage.removeItem('curietech_access_token')
}

/**
 * Builds a safe same-origin logout URL. Rejects dangerous schemes and off-origin redirects.
 * Returns the URL string or undefined if the redirect is unsafe.
 */
function buildSafeLogoutUrl(redirect?: string): string {
  const base = new URL('/api/user/logout', window.location.origin)

  if (!redirect) return base.href

  const trimmed = redirect.trim()
  if (!trimmed) return base.href

  const lower = trimmed.toLowerCase()
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:')
  ) {
    return base.href
  }
  if (trimmed.startsWith('//') || trimmed.includes('\\')) {
    return base.href
  }

  try {
    const parsed = new URL(trimmed, window.location.origin)
    if (parsed.origin !== window.location.origin || !parsed.pathname.startsWith('/')) {
      return base.href
    }
    base.searchParams.set('redirect', parsed.pathname + parsed.search + parsed.hash)
    return base.href
  } catch {
    return base.href
  }
}

/**
 * Performs logout that works in both browser and plugin mode.
 * In plugin mode: calls logout API via fetch, clears all stored credentials, reloads webview.
 * In browser mode: redirects to server-side logout endpoint.
 * @param redirect - Optional path to redirect after logout (browser mode only). Must be a safe path.
 */
export async function performLogout(redirect?: string) {
  const { bridge } = await import('./index')

  if (bridge.isInPlugin()) {
    const baseUrl = bridge.getBaseUrl()
    try {
      await fetch(`${baseUrl}/api/user/logout`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'X-Curietech-Plugin': 'true' }
      })
    } catch {
      // Best-effort: server may not respond; still clear local state
    }
    const { pluginAuthService } = await import('~/services/plugin/authService')
    await pluginAuthService.signOut()
    window.dispatchEvent(new CustomEvent('plugin-auth-logout'))
  } else {
    const safeUrl = buildSafeLogoutUrl(redirect)
    window.location.href = safeUrl
  }
}
